import { err, ok, type Result } from 'neverthrow';
import * as Sentry from '@sentry/vue';
import type { JwtPayload } from '@supabase/supabase-js';
import { CancelledError, type QueryClient } from '@tanstack/vue-query';
import { match } from 'ts-pattern';
import { RedirectError, MerchantForbiddenError, MerchantNotFoundError } from './errors';
import { getQueryDataOrPrefetch } from './getQueryDataOrPrefetch';
import { canBeMerchantSlug } from './canBeMerchantSlug';
import { reportRequestFailure } from './reportRequestFailure';
import type { PublicMerchant } from '~~/models/Merchant';
import type { MerchantRelationship } from '~~/models/MerchantRelationship';
import type { PublicProgramme } from '~~/models/Programme';
import { fetchMerchantBySlugFn } from '~/queries/useMerchantBySlugQuery';
import { fetchMerchantUserStatusFn } from '~/queries/useMerchantUserStatusQuery';
import { fetchMerchantByIdFn } from '~/queries/useMerchantQuery';
import { fetchPublicProgrammeByIdFn } from '~/queries/usePublicProgrammeQuery';
import { fetchProfileFn } from '~/queries/useProfileQuery';

export type CheckMerchantAccessParams = {
	merchant: PublicMerchant | null;
	isPublicPage: boolean;
	user: JwtPayload | null;
	relationship: MerchantRelationship | null;
};

export const checkMerchantAccess = ({
	merchant,
	isPublicPage,
	user,
	relationship,
}: CheckMerchantAccessParams): Result<PublicMerchant, RedirectError | MerchantForbiddenError | MerchantNotFoundError> => {
	if (!merchant) {
		if (!user) {
			return err(new RedirectError({ name: 'GlobalLogin' }));
		}

		return err(new MerchantNotFoundError());
	}

	if (isPublicPage) {
		return ok(merchant);
	}

	if (relationship === null) {
		return err(new RedirectError({ name: 'MerchantLogin', params: { merchantSlug: merchant.slug } }));
	}

	return match(relationship)
		.with('guest', () => err(new RedirectError({ name: 'MerchantLogin', params: { merchantSlug: merchant.slug } })))
		.with('unaffiliated', () => {
			const slugFromSignup = user?.user_metadata?.signupMerchantSlug as undefined | string;

			if (slugFromSignup === merchant.slug) {
				return err(new RedirectError({ name: 'MerchantSignupComplete', params: { merchantSlug: merchant.slug } }));
			}

			return err(new MerchantForbiddenError('unregistered'));
		})
		.with('deleted', () => err(new MerchantForbiddenError('rejected')))
		.with('needs-remapping', () => err(new RedirectError({ name: 'MerchantSignupComplete', params: { merchantSlug: merchant.slug } })))
		.with('active', () => ok(merchant))
		.exhaustive();
};

export type FetchMerchantContextParams = {
	queryClient: QueryClient;
	user: JwtPayload | null;
	merchantSlug: string;
};

export type MerchantSessionData = {
	merchant: PublicMerchant | null;
	relationship: MerchantRelationship | null;
};

export const fetchMerchantContext = async ({
	queryClient,
	merchantSlug,
	user,
}: FetchMerchantContextParams): Promise<MerchantSessionData> => {
	if (!canBeMerchantSlug(merchantSlug)) {
		Sentry.logger.info('[merchant] reserved path requested as merchant slug', { slug: merchantSlug });

		return { merchant: null, relationship: null };
	}

	const merchantData = await getQueryDataOrPrefetch({
		queryClient,
		queryKey: ['public-merchant', merchantSlug],
		queryFn: () => fetchMerchantBySlugFn(merchantSlug),
	}).unwrapOr(undefined);

	if (!merchantData) {
		return { merchant: null, relationship: null };
	}

	if (!user) {
		return { merchant: merchantData, relationship: 'guest' };
	}

	const profile = await getQueryDataOrPrefetch({
		queryClient,
		queryKey: ['profile'],
		queryFn: fetchProfileFn,
	}).unwrapOr(undefined);

	const publisherId = profile?.publisherIds?.[merchantData.id];

	if (publisherId === undefined) {
		return { merchant: merchantData, relationship: 'unaffiliated' };
	}

	// Key exists but is null: user is v2-registered but v1 signup failed
	if (publisherId === null) {
		return { merchant: merchantData, relationship: 'needs-remapping' };
	}

	// At this point, the user *was* a member, but we check if they were kicked out.
	const status = await getQueryDataOrPrefetch({
		queryClient,
		queryKey: ['merchant-user-status', merchantData.id],
		queryFn: () => fetchMerchantUserStatusFn(merchantData.id),
		ignoreSentry: true,
	});

	if (status.isErr()) {
		// The cache was cleared mid-fetch, e.g. by the sign-out after a 401
		if (status.error instanceof CancelledError) {
			return { merchant: merchantData, relationship: null };
		}

		if (status.error.message === 'Publisher not found by user id') {
			return { merchant: merchantData, relationship: 'unaffiliated' };
		}

		reportRequestFailure({
			error: status.error,
			source: 'direct',
			key: 'merchant-user-status',
			lastSuccessAt: null,
			contexts: { merchant: { id: merchantData.id, slug: merchantData.slug } },
		});

		return { merchant: merchantData, relationship: null };
	}

	if (status.value === 'Deleted') {
		return { merchant: merchantData, relationship: 'deleted' };
	}

	return { merchant: merchantData, relationship: 'active' };
};

export type FetchProgrammeContextParams = {
	queryClient: QueryClient;
	merchantId: string;
};

export const fetchProgrammeContext = async ({
	queryClient,
	merchantId,
}: FetchProgrammeContextParams): Promise<PublicProgramme | null> => {
	const merchantData = await getQueryDataOrPrefetch({
		queryClient,
		queryKey: ['merchant', merchantId],
		queryFn: () => fetchMerchantByIdFn(merchantId),
	}).unwrapOr(undefined);

	if (!merchantData || !merchantData.defaultProgrammeId) {
		return null;
	}

	const programmeData = await getQueryDataOrPrefetch({
		queryClient,
		queryKey: ['public-programme', merchantData.id, merchantData.defaultProgrammeId],
		queryFn: () => fetchPublicProgrammeByIdFn(merchantData.id, merchantData.defaultProgrammeId!),
	});

	if (programmeData.isErr()) {
		throw new Error(`Unable to fetch default programme "${merchantData.defaultProgrammeId}" for merchant "${merchantData.id}"`, { cause: programmeData.error });
	}

	return programmeData.value;
};
